import type { Step } from 'react-joyride'

interface RoleFlags {
  isAdmin: boolean
  canManageUsers: boolean
  isIntern: boolean
  isOfficeSupport: boolean
  isConsultant: boolean
}

type PortalStep = Step & {
  roleFilter?: (roles: RoleFlags) => boolean
}

const allPortalSteps: PortalStep[] = [
  {
    target: '[data-tour="sidebar-brand"]',
    title: 'Welcome to YieldOS',
    content: 'This is your consultant portal for managing farmers, recommendations, quotes, and orders. Let\'s walk through the key areas.',
    skipBeacon: true,
    placement: 'right',
  },
  {
    target: '[data-tour="nav-dashboard"]',
    title: 'Dashboard',
    content: 'Your home base. See farmer counts, soil collection progress, and recent activity at a glance.',
    skipBeacon: true,
    placement: 'right',
  },
  {
    target: '[data-tour="nav-recommendations"]',
    title: 'Recommendations',
    content: 'Create and manage product recommendations. Each recommendation walks through a questionnaire to build a customized plan for a farmer.',
    skipBeacon: true,
    placement: 'right',
  },
  {
    target: '[data-tour="nav-farmers"]',
    title: 'Farmers',
    content: 'Add and manage your farmer records. Upload soil test data here to power more accurate recommendations.',
    skipBeacon: true,
    placement: 'right',
  },
  {
    target: '[data-tour="nav-consultants"]',
    title: 'Consultants',
    content: 'View and manage consultant profiles and their assigned farmers.',
    skipBeacon: true,
    placement: 'right',
  },
  {
    target: '[data-tour="nav-quotes"]',
    title: 'Quotes',
    content: 'Create quotes from recommendations and send them to farmers for review.',
    skipBeacon: true,
    placement: 'right',
    roleFilter: (roles) => !roles.isIntern,
  },
  {
    target: '[data-tour="nav-orders"]',
    title: 'Orders',
    content: 'Track orders converted from approved quotes. Monitor fulfillment and delivery status.',
    skipBeacon: true,
    placement: 'right',
    roleFilter: (roles) => !roles.isIntern,
  },
  {
    target: '[data-tour="nav-products"]',
    title: 'Product Catalog',
    content: 'Browse the full Yield Innovations product lineup with details on each product family.',
    skipBeacon: true,
    placement: 'right',
    roleFilter: (roles) => !roles.isIntern,
  },
  {
    target: '[data-tour="nav-reports"]',
    title: 'Reports',
    content: 'Access analytics and reporting dashboards for sales performance and territory insights.',
    skipBeacon: true,
    placement: 'right',
    roleFilter: (roles) => roles.isAdmin,
  },
  {
    target: '[data-tour="nav-users"]',
    title: 'Users & Security',
    content: 'Manage user accounts, roles, and permissions. Invite new team members and control access levels.',
    skipBeacon: true,
    placement: 'right',
    roleFilter: (roles) => roles.canManageUsers,
  },
  {
    target: '[data-tour="nav-users"]',
    title: 'Employee Permissions',
    content: 'As a consultant, you can control what your sales reps can do. Edit a sales rep and toggle permissions for creating quotes, creating orders, viewing reports, and managing farmers.',
    skipBeacon: true,
    placement: 'right',
    roleFilter: (roles) => roles.isConsultant,
  },
  {
    target: '[data-tour="dashboard-stats"]',
    title: 'Quick Stats',
    content: 'These cards show your key metrics: farmer count, active quotes, and order totals. They update in real time.',
    skipBeacon: true,
    placement: 'bottom',
  },
  {
    target: '[data-tour="nav-recommendations"]',
    title: 'Creating Recommendations',
    content: 'To build a recommendation, click here and then "New Recommendation." You\'ll walk through farm info, crop selection, equipment, and optionally soil data.',
    skipBeacon: true,
    placement: 'right',
  },
  {
    target: '[data-tour="nav-farmers"]',
    title: 'Adding Farmers',
    content: 'Start by adding your farmers here. You can upload soil test reports from the farmer detail page to improve recommendation accuracy.',
    skipBeacon: true,
    placement: 'right',
  },
  {
    target: '[data-tour="nav-settings"]',
    title: 'Settings',
    content: 'Adjust your portal preferences and notification settings.',
    skipBeacon: true,
    placement: 'right',
  },
  {
    target: '[data-tour="sidebar-user"]',
    title: 'Your Account',
    content: 'Your name and role are shown here. Use the logout button when you\'re done for the day.',
    skipBeacon: true,
    placement: 'right',
  },
]

export function getPortalSteps(roles: RoleFlags): Step[] {
  return allPortalSteps
    .filter((step) => !step.roleFilter || step.roleFilter(roles))
    .map(({ roleFilter: _, ...step }) => step)
}
